import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { classes } from './ui.tsx';
import { SETTINGS_SECTION_PAGES, settingsLeafPath } from './settings/settings-routes.ts';

/**
 * The settings page's section navigator: grouped links, a filter box, and a
 * scrollspy that keeps the current section highlit.
 *
 * The page grew to a dozen cards, and "scroll until you spot it" stopped
 * working — especially for settings people visit twice a year. The filter
 * searches each section's title *and* a hidden keyword list (so "logo",
 * "tax" or "iCal" land on the right card without the word appearing in any
 * heading), and nearly every entry is a plain in-page jump: nothing to
 * deep-link, nothing that can fall out of sync with what the page renders.
 * The exception is a section that has grown into pages of its own
 * (`SETTINGS_SECTION_ROUTES`, or an entry's own `to`): its entry links there
 * instead.
 *
 * From the `md` breakpoint up it is a sticky rail beside the content; on a
 * short window it scrolls by wheel, touch and keyboard without drawing a
 * scrollbar. On a phone a compact selector stays reachable while reading a
 * long card. Search expands in normal flow so its results cannot pin over
 * the form or keyboard.
 */

export interface SettingsNavSection {
  /** Matches the section wrapper's DOM id, `settings-${id}`. */
  id: string;
  label: string;
  /** Extra search terms, comma-separated; never displayed. */
  keywords: string;
  icon: ReactNode;
  /**
   * A page this section opens on, when the page decides per company
   * (pages/settings/routes.ts, behind FEATURE_SETTINGS_HUB_ROUTES). Sections
   * that are always pages are in SETTINGS_SECTION_ROUTES instead.
   */
  to?: string;
}

export interface SettingsNavGroup {
  id: string;
  label: string;
  sections: SettingsNavSection[];
}

function SettingsNavGroupList({
  group,
  open,
  children,
}: {
  group: SettingsNavGroup;
  open: boolean;
  children: ReactNode;
}) {
  const headingClass = 'mb-1 text-xs font-semibold tracking-wide text-[color:var(--color-muted)] uppercase';
  return group.id === 'advanced' ? (
    <details open={open}>
      <summary className={`${headingClass} cursor-pointer`}>{group.label}</summary>
      {children}
    </details>
  ) : (
    <div>
      <p className={headingClass}>{group.label}</p>
      {children}
    </div>
  );
}

/**
 * Sections that are pages of their own rather than cards on `/settings`. The
 * nav links to them, and their old `#settings-<id>` hashes — bookmarks,
 * emails, the setup guide — redirect there.
 */
export const SETTINGS_SECTION_ROUTES: Readonly<Record<string, string>> = SETTINGS_SECTION_PAGES;

export function settingsSectionRoute(id: string): string | null {
  return Object.hasOwn(SETTINGS_SECTION_ROUTES, id) ? SETTINGS_SECTION_ROUTES[id]! : null;
}

/** The page a `#settings-<id>` hash now lives on, or null when it is still a card on `/settings`. */
export function settingsRouteForHash(hash: string): string | null {
  const match = /^#settings-([A-Za-z]+)$/.exec(hash);
  return match ? settingsSectionRoute(match[1]!) : null;
}

/**
 * Goes to a section wherever it lives: opens its page when it has one,
 * otherwise jumps to its card on `/settings`.
 */
export function useGoToSettingsSection(): (id: string) => void {
  const navigate = useNavigate();
  return (id: string) => {
    const route = settingsSectionRoute(id);
    if (route) navigate(route);
    else jumpToSettingsSection(id);
  };
}

/** How far below the viewport top a section counts as "current". */
const SPY_OFFSET_PX = 120;

/** While a programmatic jump is scrolling, the scrollspy keeps this section lit. */
let pinnedSettingsSectionId: string | null = null;

/** Maps a section wrapper's DOM id back to the nav section id. */
function navSectionIdFromElementId(elementId: string): string {
  if (elementId === 'integrations') return 'integrations';
  return elementId.startsWith('settings-') ? elementId.slice('settings-'.length) : elementId;
}

function elementIdForNavSection(sectionId: string): string {
  return sectionId === 'integrations' ? 'integrations' : `settings-${sectionId}`;
}

/**
 * On a phone the page scrolls in `#main`; on desktop the window scrolls.
 * `scrollIntoView` can nudge more than one ancestor, which reads as a double
 * jump on a long Settings page — scroll the one scroller that actually moves.
 */
function settingsScrollRoot(): HTMLElement | null {
  const main = document.getElementById('main');
  if (!main) return null;
  const { overflowY } = getComputedStyle(main);
  if (overflowY !== 'auto' && overflowY !== 'scroll') return null;
  return main.scrollHeight > main.clientHeight ? main : null;
}

function revealSettingsSection(element: HTMLElement) {
  if (element instanceof HTMLDetailsElement) element.open = true;
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
  }
}

function isPhoneSettingsNav(): boolean {
  // Match Tailwind's md breakpoint even with a larger browser default font.
  return !(window.matchMedia?.('(min-width: 48rem)').matches ?? window.innerWidth >= 768);
}

/** Space above a phone jump target, including the scroll container's padding. */
function settingsStickyNavHeight(): number {
  if (!isPhoneSettingsNav()) return 0;
  const nav = document.querySelector<HTMLElement>('[data-settings-nav]');
  if (!nav || nav.dataset.searchOpen === 'true') return 0;
  const root = settingsScrollRoot();
  const padding = root ? parseFloat(getComputedStyle(root).paddingTop) || 0 : 0;
  return nav.getBoundingClientRect().height + padding;
}

function scrollSettingsSectionIntoView(element: HTMLElement, behavior: ScrollBehavior = 'smooth') {
  revealSettingsSection(element);
  const navHeight = settingsStickyNavHeight();
  element.style.scrollMarginTop = navHeight ? `${navHeight + 16}px` : '';
  const root = settingsScrollRoot();
  if (root) {
    const rootRect = root.getBoundingClientRect();
    const elRect = element.getBoundingClientRect();
    const marginTop = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    const top = root.scrollTop + (elRect.top - rootRect.top) - marginTop;
    root.scrollTo({ top: Math.max(0, top), behavior });
  } else {
    element.scrollIntoView({ behavior, block: 'start' });
  }
}

/**
 * Scrolls to a section and moves the keyboard there (the wrappers carry
 * tabIndex={-1}). Shared by the nav, the setup guide and the page's
 * on-load hash handling, so every way of "going to a section" behaves the
 * same. The hash is written with replaceState rather than pushState: the
 * URL should say where you are — copyable, shareable — without the back
 * button having to step through every section visited on the way.
 */
export function jumpToSettingsSection(id: string): boolean {
  const element = document.getElementById(`settings-${id}`);
  if (!element) {
    // Settings as a hub: a section that is not on this page is a page of
    // its own, so "going to it" is opening that page.
    if (!settingsRouter?.hubRoutes) return false;
    settingsRouter.navigate(settingsLeafPath(id));
    return true;
  }
  pinnedSettingsSectionId = id;
  scrollSettingsSectionIntoView(element, 'smooth');
  element.focus({ preventScroll: true });
  // A section's own page (`/settings/billing`) already says where you are;
  // only the long page names its sections in the hash.
  if (!window.location.pathname.startsWith('/settings/')) {
    // React Router keeps { usr, key, idx } in history.state; carrying it through
    // keeps Back and the router's index arithmetic intact after a jump.
    history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}#settings-${id}`);
  }
  return true;
}

/**
 * The router, for jumpToSettingsSection: it is called from click handlers
 * all over the app (the trial banner, cards that point at another section)
 * that have no navigate of their own to pass in. Registered by the app
 * layout; absent, a jump only ever scrolls, as it always has.
 */
let settingsRouter: { hubRoutes: boolean; navigate: (to: string) => void } | null = null;

export function useSettingsRouter(): void {
  const navigate = useNavigate();
  const hubRoutes = useSession().company?.settingsHub === true;
  useEffect(() => {
    const router = { hubRoutes, navigate: (to: string) => void navigate(to) };
    settingsRouter = router;
    return () => {
      if (settingsRouter === router) settingsRouter = null;
    };
  }, [hubRoutes, navigate]);
}

/**
 * The element id a settings hash names, or null when the fragment is not one
 * of ours.
 *
 * `#integrations` is the one link minted outside the `#settings-<id>` scheme
 * (the OAuth return from connecting a mailbox). It maps to the inner card it
 * has always named.
 */
function settingsSectionIdFromHash(hash: string): string | null {
  if (hash === '#integrations') return 'integrations';
  const match = /^#settings-([A-Za-z]+)$/.exec(hash);
  return match ? `settings-${match[1]}` : null;
}

/**
 * Jumps to the section named by the URL hash once `ready` is true (the cards
 * exist), and again whenever that hash changes while Settings stays mounted.
 *
 * Native anchor scrolling cannot be trusted on a cold load: the browser tries
 * before the session query has rendered anything to scroll to. React Router
 * does not scroll to a hash on its own either, and a one-shot jump on mount
 * misses the trial banner, Back, and Forward — those change the hash without
 * mounting the page again.
 *
 * The same hash twice does not jump again. A session refresh re-renders this
 * page inside the window where the setup guide may still be pushing sections
 * down, and jumping then would yank the reader back to a section they had
 * already scrolled past. The effect depends on the hash, so that refresh does
 * not re-run it and does not cancel the corrective nudge below.
 */
export function useSettingsHashJump(ready: boolean): void {
  const { hash } = useLocation();
  const handledHash = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (handledHash.current === hash) return;
    handledHash.current = hash;

    const targetId = settingsSectionIdFromHash(hash);
    if (!targetId) return;

    // The smooth jump scrolls on its own. Those events are not the reader
    // leaving the section. A scroll once that motion has gone quiet is, and
    // so is a wheel, a touch, or a key the moment it happens — the hash does
    // not change when someone reads further down the same card, and the
    // nudge below would otherwise haul them back to the top.
    let ignoreScroll = false;
    let sawOwnScroll = false;
    let disposed = false;
    let settleTimer = 0;
    let nudgeTimer = 0;

    const targetIsInPlace = () => {
      const element = document.getElementById(targetId);
      if (!element) return false;
      const root = settingsScrollRoot();
      const expectedTop = (root?.getBoundingClientRect().top ?? 0) + settingsStickyNavHeight();
      const top = element.getBoundingClientRect().top;
      if (Math.abs(top - expectedTop) <= 80) return true;
      // A short final card cannot align at the top once scrolling is clamped.
      // That end position is settled too, so a scrollbar drag is the reader's.
      const scrollHeight = (root ?? document.scrollingElement ?? document.documentElement).scrollHeight;
      const viewportHeight = root?.clientHeight ?? window.innerHeight;
      const scrollTop = root?.scrollTop ?? window.scrollY;
      const maximum = Math.max(0, scrollHeight - viewportHeight);
      return top > expectedTop && scrollHeight > 0 && viewportHeight > 0 && Math.abs(scrollTop - maximum) <= 1;
    };

    const releaseOwnScroll = () => {
      ignoreScroll = false;
      sawOwnScroll = false;
    };

    const main = document.getElementById('main');
    const scrollTargets: EventTarget[] = main ? [window, main] : [window];

    const detach = () => {
      if (disposed) return;
      disposed = true;
      window.clearTimeout(settleTimer);
      for (const target of scrollTargets) {
        target.removeEventListener('scroll', onScroll);
        target.removeEventListener('scrollend', onScrollEnd);
      }
      window.removeEventListener('wheel', cancelNudge);
      window.removeEventListener('touchstart', cancelNudge);
      window.removeEventListener('keydown', cancelNudge);
    };

    function cancelNudge() {
      window.clearTimeout(nudgeTimer);
      detach();
    }

    function onScroll() {
      if (ignoreScroll) {
        sawOwnScroll = true;
        // The jump is still moving. Wait until it has been quiet, for
        // browsers that never fire scrollend, before the next scroll counts
        // as the reader's.
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          if (!disposed && targetIsInPlace()) releaseOwnScroll();
        }, 150);
        return;
      }
      cancelNudge();
    }

    function onScrollEnd() {
      window.clearTimeout(settleTimer);
      // Loading cards can move the destination while the motion is running.
      if (targetIsInPlace()) releaseOwnScroll();
    }

    // Next frame, so the section wrappers have been laid out first.
    const frame = requestAnimationFrame(() => {
      if (disposed) return;
      const element = document.getElementById(targetId);
      if (!element) return;
      ignoreScroll = true;
      pinnedSettingsSectionId = navSectionIdFromElementId(targetId);
      scrollSettingsSectionIntoView(element, 'smooth');
      element.focus({ preventScroll: true });
      // Already in place: nothing scrolls and nothing ends, so the next
      // scroll belongs to the reader.
      settleTimer = window.setTimeout(() => {
        // Chrome can start the smooth motion after this fallback expires.
        // Silence only means "already in place" when the target is there.
        if (!disposed && !sawOwnScroll && targetIsInPlace()) releaseOwnScroll();
      }, 50);
    });

    // The setup guide mounts above everything once its queries answer,
    // pushing every section down after the jump has already landed. One
    // corrective nudge, instant rather than smooth, if the target has
    // drifted from the viewport top by then. The hash guard leaves a reader
    // who has since moved to another section alone — the nav writes its hash
    // with replaceState, which does not re-run this effect.
    nudgeTimer = window.setTimeout(() => {
      if (disposed) return;
      if (window.location.hash !== hash) {
        detach();
        return;
      }
      const element = document.getElementById(targetId);
      const expectedTop = (settingsScrollRoot()?.getBoundingClientRect().top ?? 0) + settingsStickyNavHeight();
      const drifted = element !== null && Math.abs(element.getBoundingClientRect().top - expectedTop) > 80;
      // Drop the listeners first, so this scroll is not taken for the reader's.
      detach();
      if (drifted && element) scrollSettingsSectionIntoView(element, 'auto');
    }, 1200);

    for (const target of scrollTargets) {
      target.addEventListener('scroll', onScroll, { passive: true });
      target.addEventListener('scrollend', onScrollEnd);
    }
    window.addEventListener('wheel', cancelNudge, { passive: true });
    window.addEventListener('touchstart', cancelNudge, { passive: true });
    window.addEventListener('keydown', cancelNudge);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(nudgeTimer);
      detach();
      // Strict Mode runs this cleanup and then the effect again, on the same
      // instance. Forgetting the hash lets that second run jump.
      if (handledHash.current === hash) handledHash.current = null;
    };
  }, [ready, hash]);
}

export function SettingsNav({ groups }: { groups: SettingsNavGroup[] }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchId = useId();
  const searchInput = useRef<HTMLInputElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  const phoneControls = useRef<HTMLDivElement>(null);
  const focusedControl = useRef<HTMLElement | null>(null);
  const pendingJump = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const media = window.matchMedia?.('(min-width: 48rem)');
    if (!media?.addEventListener) return;
    const onChange = () => {
      if (media.matches && phoneControls.current?.contains(focusedControl.current)) {
        searchInput.current?.focus({ preventScroll: true });
      } else if (!media.matches && searchInput.current?.parentElement?.contains(focusedControl.current)) {
        // A focused desktop search must stay visible after phone rotation.
        setSearchOpen(true);
      }
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useLayoutEffect(() => {
    if (searchOpen) {
      searchInput.current?.focus();
    } else if (pendingJump.current) {
      // Measure after the compact bar is restored, not the expanded results.
      const id = pendingJump.current;
      pendingJump.current = null;
      if (jumpToSettingsSection(id)) setActive(id);
    }
  }, [searchOpen]);
  const sections = useMemo(() => groups.flatMap((group) => group.sections), [groups]);
  const [active, setActive] = useState<string | null>(sections[0]?.id ?? null);

  // Scrollspy: the current section is the last one whose top has passed the
  // offset line. A plain scroll listener (throttled to animation frames)
  // rather than IntersectionObserver, because "last heading above the line"
  // is the actual question and IO answers a different one.
  useEffect(() => {
    let frame = 0;
    const compute = () => {
      frame = 0;
      const offset =
        (settingsScrollRoot()?.getBoundingClientRect().top ?? 0) +
        Math.max(SPY_OFFSET_PX, settingsStickyNavHeight() + 16);
      if (pinnedSettingsSectionId) {
        const pinned = document.getElementById(elementIdForNavSection(pinnedSettingsSectionId));
        const top = pinned?.getBoundingClientRect().top;
        // A downward jump arrives when the section top reaches the spy line.
        // An upward jump starts with that top already above the line, so
        // `<= offset` is true on the first scroll and the highlight would
        // walk back through every section in between. The arrival band is
        // the line itself, not everything above it.
        const arrived = top !== undefined && top <= offset && top >= offset - SPY_OFFSET_PX;
        if (!pinned || arrived) {
          pinnedSettingsSectionId = null;
        } else {
          setActive(pinnedSettingsSectionId);
          return;
        }
      }
      let current = sections[0]?.id ?? null;
      let closestTop = -Infinity;
      for (const section of sections) {
        const element = document.getElementById(`settings-${section.id}`);
        if (!element || element.closest('details:not([open])')) continue;
        const top = element.getBoundingClientRect().top;
        // Use actual page position instead of the navigator's group order.
        if (top <= offset && top >= closestTop) {
          current = section.id;
          closestTop = top;
        }
      }
      setActive(current);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(compute);
    };
    // On a phone the page scrolls in `#main`, and that event does not bubble
    // to the window. Desktop still scrolls the window, so both are watched.
    const main = document.getElementById('main');
    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    main?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      main?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [sections]);

  const needle = query.trim().toLowerCase();
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      sections: group.sections.filter(
        (section) =>
          needle === '' ||
          section.label.toLowerCase().includes(needle) ||
          section.keywords.toLowerCase().includes(needle),
      ),
    }))
    .filter((group) => group.sections.length > 0);

  function jump(id: string) {
    const route = sections.find((section) => section.id === id)?.to ?? settingsSectionRoute(id);
    if (route) {
      navigate(route);
    } else if (searchOpen && isPhoneSettingsNav()) {
      pendingJump.current = id;
      setQuery('');
      setSearchOpen(false);
    } else if (jumpToSettingsSection(id)) setActive(id);
  }

  return (
    <nav
      aria-label={t('settings.nav.ariaLabel')}
      data-settings-nav=""
      data-search-open={searchOpen}
      onFocusCapture={(event) => {
        focusedControl.current = event.target;
      }}
      onBlurCapture={(event) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        // A breakpoint can hide the focused control before matchMedia fires.
        // Retain that control only for this visibility-driven blur.
        if (event.relatedTarget || event.target.getClientRects().length > 0) focusedControl.current = null;
      }}
      className={classes(
        searchOpen ? 'relative' : 'sticky',
        'scrollbar-none top-0 z-20 -mx-4 mb-4 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-4 py-2 md:sticky md:top-6 md:z-auto md:mx-0 md:mb-0 md:max-h-[calc(100dvh-3rem)] md:overflow-y-auto md:border-0 md:bg-transparent md:p-0 md:pr-1',
      )}
    >
      <div ref={phoneControls} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 md:hidden">
        <label className="min-w-0">
          <span className="sr-only">{t('settings.nav.jumpLabel')}</span>
          <select className="field-control" value={active ?? ''} onChange={(event) => jump(event.target.value)}>
            {groups.map((group) => (
              <optgroup key={group.id} label={group.label}>
                {group.sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <button
          ref={searchButton}
          type="button"
          className="btn btn-secondary h-11 w-11 px-0"
          aria-label={t('settings.nav.searchLabel')}
          title={t('settings.nav.searchLabel')}
          aria-expanded={searchOpen}
          aria-controls={searchId}
          onClick={() => setSearchOpen((open) => !open)}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            {searchOpen ? (
              <path d="m6 6 12 12M6 18 18 6" />
            ) : (
              <>
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="m16 16 4 4" />
              </>
            )}
          </svg>
        </button>
      </div>

      <div
        id={searchId}
        className={classes(searchOpen ? 'block' : 'hidden', 'mt-3 md:mt-0 md:block')}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && searchOpen && isPhoneSettingsNav()) {
            event.preventDefault();
            setSearchOpen(false);
            searchButton.current?.focus();
          }
        }}
      >
        <input
          ref={searchInput}
          type="search"
          className="field-control mb-3"
          placeholder={t('settings.nav.searchPlaceholder')}
          aria-label={t('settings.nav.searchLabel')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {visibleGroups.length === 0 ? <p className="field-hint">{t('settings.nav.noMatches')}</p> : null}

        <div
          className={classes(
            needle ? 'flex' : 'hidden',
            'max-h-64 flex-col gap-y-4 overflow-y-auto md:flex md:max-h-none md:overflow-visible',
          )}
        >
          {visibleGroups.map((group) => (
            <SettingsNavGroupList
              key={`${group.id}:${Boolean(needle)}`}
              group={group}
              open={Boolean(needle) || group.sections.some((section) => section.id === active)}
            >
              <ul className="flex flex-col gap-1">
                {group.sections.map((section) => {
                  const route = section.to ?? settingsSectionRoute(section.id);
                  const entryClass = classes(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm whitespace-nowrap',
                    active === section.id && !route
                      ? 'bg-[color:var(--color-brand-soft)] font-semibold text-[color:var(--color-brand-strong)]'
                      : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-ink)]',
                  );
                  const content = (
                    <>
                      <span aria-hidden="true" className="shrink-0">
                        {section.icon}
                      </span>
                      {section.label}
                    </>
                  );
                  return (
                    <li key={section.id} className="shrink-0">
                      {/* A section with its own page is a link there: it can open
                          in a new tab, and it is never the card in view. */}
                      {route ? (
                        <Link to={route} className={entryClass}>
                          {content}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => jump(section.id)}
                          aria-current={active === section.id ? 'true' : undefined}
                          className={entryClass}
                        >
                          {content}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </SettingsNavGroupList>
          ))}
        </div>
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Icons — small line glyphs so the rail can be scanned by shape as well as
// by word. Decorative only (aria-hidden above); the label carries the meaning.
// ---------------------------------------------------------------------------

const NAV_ICON_PATHS: Record<string, ReactNode> = {
  company: <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />,
  pricing: (
    <>
      <path d="M20.6 13.4L12 22 2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </>
  ),
  locations: (
    <>
      <path d="M12 21s-6-5.2-6-10a6 6 0 0 1 12 0c0 4.8-6 10-6 10z" />
      <circle cx="12" cy="11" r="2" />
    </>
  ),
  library: <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
  roomTypes: <path d="M4 21V4h11v17M15 6h5v15M2 21h20M11 12v1.5" />,
  layouts: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M3 12h9M12 3v18M12 15h9" />
    </>
  ),
  checklists: <path d="M10 6h10M10 12h10M10 18h10M4 6l1.5 1.5L8 5M4 12l1.5 1.5L8 11M4 18l1.5 1.5L8 17" />,
  widget: <path d="M8 6l-5 6 5 6M16 6l5 6-5 6" />,
  photoQuotes: (
    <>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  hours: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  recurringHorizon: (
    <>
      <path d="M4 12a8 8 0 0 1 13.7-5.7L20 8.6" />
      <path d="M20 4v4.6h-4.6" />
      <path d="M20 12a8 8 0 0 1-13.7 5.7L4 15.4" />
      <path d="M4 20v-4.6h4.6" />
    </>
  ),
  fillSlots: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4M12 13v5M9.5 15.5h5" />
    </>
  ),
  reminders: <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10 19a2 2 0 0 0 4 0" />,
  lateCancel: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2M8 19l8-2" />
    </>
  ),
  notifications: (
    <>
      <path d="M3 7l9 6 9-6" />
      <rect x="3" y="5" width="18" height="14" rx="2" />
    </>
  ),
  followups: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />,
  reengagement: (
    <>
      <path d="M4 12a8 8 0 1 0 2.3-5.7L4 8.6" />
      <path d="M4 4v4.6h4.6" />
      <path d="M9 12h6M12 9v6" />
    </>
  ),
  messaging: <path d="M4 4h16v12H9l-5 4z" />,
  texting: <path d="M4 4h16v12H9l-5 4zM8 9h8M8 12h5" />,
  calls: (
    <>
      <path d="M8 3h3l1.2 3-2 .8a8 8 0 0 0 4 4l.8-2L18 10v3a2 2 0 0 1-2 2A13 13 0 0 1 6 5a2 2 0 0 1 2-2z" />
      <path d="M15 3a6 6 0 0 1 6 6M15 6.5A2.5 2.5 0 0 1 17.5 9" />
    </>
  ),
  jobChat: (
    <>
      <path d="M3 4h12v8H7l-4 3z" />
      <path d="M18 8h3v11l-3-3h-7v-4" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  billing: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  payments: (
    <>
      <rect x="2" y="7" width="20" height="10" rx="1" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  paymentMethods: (
    <>
      <circle cx="9" cy="12" r="6" />
      <path d="M14.5 6.6a6 6 0 0 1 0 10.8M9 9.5v5M7.2 12h3.6" />
    </>
  ),
  numbering: <path d="M9 4L7 20M17 4l-2 16M4 9h16M3 15h16" />,
  tips: (
    <>
      <circle cx="12" cy="6.5" r="3.2" />
      <path d="M12 5.1v2.8M10.6 6.5h2.8" />
      <path d="M4.5 16.6h3.4l2.8 2.2h6.4a1.9 1.9 0 0 0 0-3.8h-4.7" />
    </>
  ),
  teamPay: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M12 9v6M10.2 10.4c0-.8.8-1.4 1.8-1.4s1.8.6 1.8 1.2-.6 1-1.8 1.3-1.8.7-1.8 1.4.8 1.2 1.8 1.2 1.8-.5 1.8-1.3" />
    </>
  ),
  team: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5M16 5.2a3 3 0 0 1 0 5.6M17.5 15.4c2.1.7 3.5 2.2 3.5 4.6" />
    </>
  ),
  account: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </>
  ),
  security: (
    <>
      <path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  referrals: (
    <>
      <rect x="3" y="8" width="18" height="13" rx="1" />
      <path d="M3 12h18M12 8v13M12 8c-1.5-3-5-3.5-5-1.5S10 8 12 8zM12 8c1.5-3 5-3.5 5-1.5S14 8 12 8z" />
    </>
  ),
  contact: (
    <>
      <path d="M8 3h3l1.2 3-2 .8a8 8 0 0 0 4 4l.8-2L18 10v3a2 2 0 0 1-2 2A13 13 0 0 1 6 5a2 2 0 0 1 2-2z" />
    </>
  ),
  data: <path d="M12 3v12M7 10l5 5 5-5M4 21h16" />,
  customerImport: <path d="M12 15V3M7 8l5-5 5 5M4 21h16M4 15v6M20 15v6" />,
  logs: <path d="M4.6 15a8 8 0 1 0 .9-7.5M4 4v4h4M12 8v4.5l3 2" />,
  ai: (
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7z" />
  ),
  aiAssistants: (
    <>
      <rect x="4" y="7" width="16" height="13" rx="3" />
      <path d="M12 3v4M9 13v1M15 13v1M9.5 17h5" />
    </>
  ),
  apiKeys: (
    <>
      <circle cx="8" cy="12" r="3.5" />
      <path d="M11.5 12H21M18 12v3M15 12v2" />
    </>
  ),
  apiDocs: (
    <>
      <path d="M4 4h9l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M13 4v5h5" />
      <path d="M8.5 13.5L7 15l1.5 1.5M13 13.5L14.5 15 13 16.5" />
    </>
  ),
  delete: (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
};

export function SettingsNavIcon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {NAV_ICON_PATHS[name] ?? <circle cx="12" cy="12" r="8" />}
    </svg>
  );
}
